import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { type GasCurvePoint, type KguParamsWrite, parametersApi } from "@/api/parameters";
import { useCanEdit, useRole } from "@/hooks/useRole";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Backend повертає `HH:MM:SS`, інпут типу `time` — `HH:MM`. */
function trimSeconds(t: string): string {
  return t.length >= 5 ? t.slice(0, 5) : t;
}

interface PointDraft { power_kw: string; gas_m3h: string }

export function KguSettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useCanEdit(["admin", "super_admin"]);
  const role = useRole();
  const isSuperAdmin = role === "super_admin";

  const params = useQuery({ queryKey: ["kgu-params"], queryFn: () => parametersApi.getKguParams() });
  const curve = useQuery({ queryKey: ["gas-curve"], queryFn: () => parametersApi.getGasCurve() });

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy-700">{t("kgu_settings.title")}</h1>
        <p className="text-sm text-slate-500">{t("kgu_settings.subtitle")}</p>
        {!canEdit && (
          <div className="mt-2 text-xs text-amber-600">{t("common.read_only")}</div>
        )}
      </div>

      {params.isLoading || curve.isLoading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
        </div>
      ) : (
        <>
          <KguParamsForm
            initial={params.data}
            canEdit={canEdit}
            isSuperAdmin={isSuperAdmin}
            onSaved={() => qc.invalidateQueries({ queryKey: ["kgu-params"] })}
          />

          <GasCurveSection
            initial={curve.data ?? []}
            canEdit={canEdit}
            onSaved={() => qc.invalidateQueries({ queryKey: ["gas-curve"] })}
          />
        </>
      )}
    </div>
  );
}

// ---------- KGU params form ----------

interface KguParamsFormProps {
  initial: Awaited<ReturnType<typeof parametersApi.getKguParams>> | undefined;
  canEdit: boolean;
  isSuperAdmin: boolean;
  onSaved: () => void;
}

const DEFAULTS = {
  effective_from: todayIso(),
  power_min_kw: 750,
  power_max_kw: 1500,
  power_step_kw: 10,
  self_needs_estimate_kw: 20,
  switching_time_min: 4,
  balance_tolerance_pct: 2,
  heat_balance_tolerance_pct: 4,
  direction_anomaly_threshold_kwh: 1,
  imbalance_pos_discount_pct: 5,
  imbalance_neg_penalty_pct: 5,
  rdn_first_attempt_at: "13:20",
  rdn_retry_until: "17:00",
  period_close_day: 15,
  plan_submit_deadline: "09:00",
  plan_reminder_at: "08:30",
  has_bess: false,
};

function buildForm(initial: KguParamsFormProps["initial"]): KguParamsWrite {
  if (!initial) return DEFAULTS;
  return {
    effective_from: initial.effective_from,
    power_min_kw: Number(initial.power_min_kw),
    power_max_kw: Number(initial.power_max_kw),
    power_step_kw: Number(initial.power_step_kw),
    self_needs_estimate_kw: Number(initial.self_needs_estimate_kw),
    switching_time_min: initial.switching_time_min,
    balance_tolerance_pct: Number(initial.balance_tolerance_pct),
    heat_balance_tolerance_pct: Number(initial.heat_balance_tolerance_pct),
    direction_anomaly_threshold_kwh: Number(initial.direction_anomaly_threshold_kwh),
    imbalance_pos_discount_pct: Number(initial.imbalance_pos_discount_pct),
    imbalance_neg_penalty_pct: Number(initial.imbalance_neg_penalty_pct),
    rdn_first_attempt_at: trimSeconds(initial.rdn_first_attempt_at),
    rdn_retry_until: trimSeconds(initial.rdn_retry_until),
    period_close_day: initial.period_close_day,
    plan_submit_deadline: trimSeconds(initial.plan_submit_deadline),
    plan_reminder_at: trimSeconds(initial.plan_reminder_at),
    has_bess: initial.has_bess,
  };
}

function KguParamsForm({ initial, canEdit, isSuperAdmin, onSaved }: KguParamsFormProps) {
  const { t } = useTranslation();

  const [form, setForm] = useState<KguParamsWrite>(() => buildForm(initial));

  // Гідратація форми при зміні initial (наприклад, після switch-tenant
  // refetch повертає інші параметри). Без цього useState ініціалізується
  // лише раз — і користувач бачить чужий тенант.
  useEffect(() => {
    if (initial) setForm(buildForm(initial));
  }, [initial?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () =>
      parametersApi.putKguParams({
        ...form,
        rdn_first_attempt_at: form.rdn_first_attempt_at + ":00",
        rdn_retry_until: form.rdn_retry_until + ":00",
        plan_submit_deadline: form.plan_submit_deadline + ":00",
        plan_reminder_at: form.plan_reminder_at + ":00",
      }),
    onSuccess: () => onSaved(),
  });

  const set = <K extends keyof KguParamsWrite>(k: K, v: KguParamsWrite[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const num = (v: string): number => (v === "" ? 0 : Number(v));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("kgu_settings.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FieldGroup>
          <Field label={t("kgu_settings.effective_from")}>
            <Input
              type="date"
              value={form.effective_from}
              onChange={(e) => set("effective_from", e.target.value)}
              disabled={!canEdit}
            />
          </Field>
        </FieldGroup>

        <Section title={t("kgu_settings.section_power")}>
          <FieldGroup cols={3}>
            <Field label={t("kgu_settings.power_min_kw")}>
              <Input type="number" step="1" value={form.power_min_kw}
                onChange={(e) => set("power_min_kw", num(e.target.value))} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.power_max_kw")}>
              <Input type="number" step="1" value={form.power_max_kw}
                onChange={(e) => set("power_max_kw", num(e.target.value))} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.power_step_kw")}>
              <Input type="number" step="1" value={form.power_step_kw}
                onChange={(e) => set("power_step_kw", num(e.target.value))} disabled={!canEdit} />
            </Field>
          </FieldGroup>
          <FieldGroup cols={2}>
            <Field label={t("kgu_settings.self_needs_estimate_kw")} hint={t("kgu_settings.self_needs_hint")}>
              <Input type="number" step="0.1" value={form.self_needs_estimate_kw}
                onChange={(e) => set("self_needs_estimate_kw", num(e.target.value))} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.switching_time_min")}>
              <Input type="number" step="1" value={form.switching_time_min}
                onChange={(e) => set("switching_time_min", Math.round(num(e.target.value)))} disabled={!canEdit} />
            </Field>
          </FieldGroup>
        </Section>

        <Section title={t("kgu_settings.section_tolerances")}>
          <FieldGroup cols={3}>
            <Field label={t("kgu_settings.balance_tolerance_pct")}>
              <Input type="number" step="0.1" value={form.balance_tolerance_pct}
                onChange={(e) => set("balance_tolerance_pct", num(e.target.value))} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.heat_balance_tolerance_pct")} hint={t("kgu_settings.heat_tolerance_hint")}>
              <Input type="number" step="0.1" value={form.heat_balance_tolerance_pct}
                onChange={(e) => set("heat_balance_tolerance_pct", num(e.target.value))} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.direction_anomaly_threshold_kwh")} hint={t("kgu_settings.direction_anomaly_hint")}>
              <Input type="number" step="0.1" value={form.direction_anomaly_threshold_kwh}
                onChange={(e) => set("direction_anomaly_threshold_kwh", num(e.target.value))} disabled={!canEdit} />
            </Field>
          </FieldGroup>
        </Section>

        <Section title={t("kgu_settings.section_imbalance")}>
          <FieldGroup cols={2}>
            <Field label={t("kgu_settings.imbalance_pos_discount_pct")}>
              <Input type="number" step="0.1" value={form.imbalance_pos_discount_pct}
                onChange={(e) => set("imbalance_pos_discount_pct", num(e.target.value))} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.imbalance_neg_penalty_pct")}>
              <Input type="number" step="0.1" value={form.imbalance_neg_penalty_pct}
                onChange={(e) => set("imbalance_neg_penalty_pct", num(e.target.value))} disabled={!canEdit} />
            </Field>
          </FieldGroup>
        </Section>

        <Section title={t("kgu_settings.section_rdn")} hint={t("kgu_settings.rdn_hint")}>
          <FieldGroup cols={2}>
            <Field label={t("kgu_settings.rdn_first_attempt_at")}>
              <Input type="time" value={form.rdn_first_attempt_at}
                onChange={(e) => set("rdn_first_attempt_at", e.target.value)} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.rdn_retry_until")}>
              <Input type="time" value={form.rdn_retry_until}
                onChange={(e) => set("rdn_retry_until", e.target.value)} disabled={!canEdit} />
            </Field>
          </FieldGroup>
        </Section>

        <Section title={t("kgu_settings.section_plan")}>
          <FieldGroup cols={2}>
            <Field label={t("kgu_settings.plan_submit_deadline")}>
              <Input type="time" value={form.plan_submit_deadline}
                onChange={(e) => set("plan_submit_deadline", e.target.value)} disabled={!canEdit} />
            </Field>
            <Field label={t("kgu_settings.plan_reminder_at")}>
              <Input type="time" value={form.plan_reminder_at}
                onChange={(e) => set("plan_reminder_at", e.target.value)} disabled={!canEdit} />
            </Field>
          </FieldGroup>
        </Section>

        <Section title={t("kgu_settings.section_period")}>
          <FieldGroup>
            <Field label={t("kgu_settings.period_close_day")} hint={t("kgu_settings.period_close_hint")}>
              <Input type="number" min={1} max={28} step="1" value={form.period_close_day}
                onChange={(e) => set("period_close_day", Math.round(num(e.target.value)))}
                disabled={!isSuperAdmin} />
            </Field>
          </FieldGroup>
        </Section>

        <Section title={t("kgu_settings.section_other")}>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.has_bess}
              onChange={(e) => set("has_bess", e.target.checked)} disabled={!canEdit} />
            {t("kgu_settings.has_bess")}
          </label>
        </Section>

        {canEdit && (
          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
            {save.isSuccess && <span className="text-sm text-brand-mint-600">{t("common.saved")}</span>}
            {save.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Gas curve ----------

interface GasCurveSectionProps {
  initial: GasCurvePoint[];
  canEdit: boolean;
  onSaved: () => void;
}

function GasCurveSection({ initial, canEdit, onSaved }: GasCurveSectionProps) {
  const { t } = useTranslation();

  const [effFrom, setEffFrom] = useState(
    initial[0]?.effective_from ?? todayIso(),
  );
  const [points, setPoints] = useState<PointDraft[]>(() =>
    initial.length > 0
      ? initial.map((p) => ({ power_kw: String(p.power_kw), gas_m3h: String(p.gas_m3h) }))
      : [
          { power_kw: "750", gas_m3h: "1990" },
          { power_kw: "1125", gas_m3h: "2830" },
          { power_kw: "1500", gas_m3h: "3620" },
        ],
  );

  useEffect(() => {
    if (initial.length > 0) {
      setEffFrom(initial[0].effective_from);
      setPoints(
        initial.map((p) => ({ power_kw: String(p.power_kw), gas_m3h: String(p.gas_m3h) })),
      );
    }
  }, [initial]);

  const chartData = useMemo(
    () =>
      points
        .map((p) => ({ power_kw: Number(p.power_kw), gas_m3h: Number(p.gas_m3h) }))
        .filter((p) => Number.isFinite(p.power_kw) && Number.isFinite(p.gas_m3h))
        .sort((a, b) => a.power_kw - b.power_kw),
    [points],
  );

  const valid = points.length >= 3 && points.every((p) => p.power_kw && p.gas_m3h);

  const save = useMutation({
    mutationFn: () =>
      parametersApi.putGasCurve({
        effective_from: effFrom,
        points: points.map((p) => ({
          power_kw: Number(p.power_kw),
          gas_m3h: Number(p.gas_m3h),
        })),
      }),
    onSuccess: () => onSaved(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("kgu_settings.section_gas_curve")}</CardTitle>
        <CardDescription>{t("kgu_settings.gas_curve_hint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGroup>
          <Field label={t("kgu_settings.effective_from")}>
            <Input type="date" value={effFrom}
              onChange={(e) => setEffFrom(e.target.value)} disabled={!canEdit} />
          </Field>
        </FieldGroup>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="p-2">{t("kgu_settings.gas_curve_power")}</th>
                <th className="p-2">{t("kgu_settings.gas_curve_gas")}</th>
                {canEdit && <th className="p-2 w-12" />}
              </tr>
            </thead>
            <tbody>
              {points.map((p, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-2">
                    <Input type="number" step="0.1" value={p.power_kw}
                      onChange={(e) =>
                        setPoints((s) => s.map((it, j) => (i === j ? { ...it, power_kw: e.target.value } : it)))
                      } disabled={!canEdit} />
                  </td>
                  <td className="p-2">
                    <Input type="number" step="0.1" value={p.gas_m3h}
                      onChange={(e) =>
                        setPoints((s) => s.map((it, j) => (i === j ? { ...it, gas_m3h: e.target.value } : it)))
                      } disabled={!canEdit} />
                  </td>
                  {canEdit && (
                    <td className="p-2">
                      <Button variant="ghost" size="icon" type="button"
                        onClick={() => setPoints((s) => s.filter((_, j) => j !== i))}
                        disabled={points.length <= 3}
                      ><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canEdit && (
          <Button variant="outline" onClick={() => setPoints((s) => [...s, { power_kw: "", gas_m3h: "" }])}>
            <Plus className="h-4 w-4" /> {t("kgu_settings.gas_curve_add")}
          </Button>
        )}

        {/* Chart preview */}
        {chartData.length >= 2 && (
          <div className="h-64 mt-4 border border-slate-100 rounded-md p-2">
            <div className="text-xs text-slate-500 mb-1">{t("kgu_settings.gas_curve_chart_title")}</div>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="power_kw" type="number" domain={["auto", "auto"]}
                  label={{ value: "кВт", position: "insideBottomRight", offset: -4, fill: "#64748b" }} />
                <YAxis label={{ value: "м³/год", angle: -90, position: "insideLeft", fill: "#64748b" }} />
                <Tooltip />
                <Line dataKey="gas_m3h" stroke="#002060" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {canEdit && (
          <div className="flex items-center gap-3">
            <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
            {save.isSuccess && <span className="text-sm text-brand-mint-600">{t("common.saved")}</span>}
            {save.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- helpers ----------

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-slate-800">{title}</h3>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function FieldGroup({ children, cols = 1 }: { children: React.ReactNode; cols?: 1 | 2 | 3 }) {
  const colsClass = cols === 3 ? "grid-cols-3" : cols === 2 ? "grid-cols-2" : "grid-cols-1";
  return <div className={`grid gap-3 ${colsClass}`}>{children}</div>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
